import React, { useState, useMemo } from "react";
import {
  Stack,
  Box,
  Typography,
  Paper,
  Skeleton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";

import { MetricCard, ChartCard } from "../shared";
import InfoButton from "../Clinical/InfoButton";
import {
  formatCost,
  formatNumber,
  COST_CHART_COLORS,
  COST_LINE_COLORS,
  COST_BAR_COLORS,
} from "../../constants/ceoDashboard";

const INITIAL_ROWS = 5;

// ── Month label formatter ──────────────────────────────────────
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatMonthLabel(monthStr) {
  const [y, m] = monthStr.split("-");
  return MONTH_NAMES[parseInt(m, 10) - 1] + " '" + y.slice(2);
}

/**
 * Panel 2 - Cost Analysis (v2)
 * Multiple views: All (donut), Monthly (line), By Location (bar).
 * Data split: cost.summary, cost.monthly, cost.byLocation, cost.topPCPs, cost.topFacilities.
 */
export default function PanelCostAnalysis({ data, loading, helpContent }) {
  const [activeView, setActiveView] = useState("all");
  const [pcpExpanded, setPcpExpanded] = useState(false);
  const [facilityExpanded, setFacilityExpanded] = useState(false);

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header skeleton */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width={36} height={36} animation="wave" />
          <Skeleton variant="text" width={180} height={32} animation="wave" />
        </Stack>

        {/* Toggle buttons skeleton */}
        <Skeleton
          variant="rounded"
          width={260}
          height={36}
          animation="wave"
          sx={{ mb: 2 }}
        />

        {/* KPI cards skeleton */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            mb: 3,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <MetricCard key={i} loading />
          ))}
        </Box>

        {/* Chart + tables skeleton */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1.5fr" },
          }}
        >
          <Skeleton
            variant="rectangular"
            height={300}
            animation="wave"
            sx={{ borderRadius: 3 }}
          />
          <Skeleton
            variant="rectangular"
            height={300}
            animation="wave"
            sx={{ borderRadius: 3 }}
          />
        </Box>
      </Paper>
    );
  }

  if (!data) return null;

  const { summary, monthly, byLocation, topPCPs, topFacilities } = data;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: "success.main",
            color: "white",
          }}
        >
          <AttachMoneyIcon fontSize="small" />
        </Box>
        <Typography variant="h6" fontWeight={700}>
          Cost Analysis
        </Typography>
        <InfoButton helpContent={helpContent} />
      </Stack>

      {/* ── Toggle Buttons ─────────────────────────────────────── */}
      <ToggleButtonGroup
        value={activeView}
        exclusive
        onChange={(_, val) => val && setActiveView(val)}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all" sx={{ textTransform: "none", px: 2 }}>
          All
        </ToggleButton>
        <ToggleButton value="monthly" sx={{ textTransform: "none", px: 2 }}>
          Monthly
        </ToggleButton>
        <ToggleButton value="byLocation" sx={{ textTransform: "none", px: 2 }}>
          By Location
        </ToggleButton>
      </ToggleButtonGroup>

      {/* ── KPI Cards ───────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          mb: 3,
        }}
      >
        <MetricCard
          title="Total Cost of Care"
          value={formatCost(summary?.totalCostOfCare)}
          icon={<AttachMoneyIcon />}
          color="primary"
        />
        <MetricCard
          title="High Cost Patients"
          value={formatNumber(summary?.highCostPatients)}
          icon={<PeopleIcon />}
          color="warning"
        />
        <MetricCard
          title="Avg Cost / Patient"
          value={formatCost(summary?.avgCostPerPatient)}
          icon={<AccountBalanceIcon />}
          color="info"
        />
        <MetricCard
          title="New High Cost"
          value={formatNumber(summary?.newHighCost)}
          icon={<TrendingUpIcon />}
          color="error"
        />
      </Box>

      {/* ── Chart + Tables ──────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1.5fr" },
          alignItems: "start",
        }}
      >
        {/* LEFT: Chart area (view-dependent) */}
        {activeView === "all" && <AllView summary={summary} />}
        {activeView === "monthly" && <MonthlyView monthly={monthly} />}
        {activeView === "byLocation" && <ByLocationView byLocation={byLocation} />}

        {/* RIGHT: Two stacked tables */}
        <Stack spacing={2}>
          <ExpandableTable
            title="Top PCPs by Cost"
            columns={["PCP Name", "Members", "Total Cost"]}
            rows={topPCPs?.pcps || []}
            renderRow={(row, idx) => (
              <TableRow
                key={idx}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <TableCell sx={cellSx}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {row.pcpName}
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {formatNumber(row.memberCount)}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCost(row.totalCost)}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            expanded={pcpExpanded}
            onToggle={() => setPcpExpanded((prev) => !prev)}
          />

          <ExpandableTable
            title="Top Facilities"
            columns={["Facility", "Claims", "Total Cost"]}
            rows={topFacilities?.facilities || []}
            renderRow={(row, idx) => (
              <TableRow
                key={idx}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <TableCell sx={cellSx}>
                  {row.facility === "UNKNOWN" ? (
                    <Tooltip
                      title="Claims without assigned facility"
                      arrow
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          maxWidth: 200,
                          fontStyle: "italic",
                          color: "text.secondary",
                          cursor: "help",
                        }}
                      >
                        {row.facility}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ maxWidth: 200 }}
                    >
                      {row.facility}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {formatNumber(row.claimCount)}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCost(row.totalCost)}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            expanded={facilityExpanded}
            onToggle={() => setFacilityExpanded((prev) => !prev)}
          />
        </Stack>
      </Box>
    </Paper>
  );
}

// ── Shared table cell styles ────────────────────────────────────
const cellSx = { py: 0.75, px: 1.5, fontSize: "0.8rem" };
const headerCellSx = {
  ...cellSx,
  fontWeight: 700,
  bgcolor: "action.hover",
  borderBottom: 2,
  borderColor: "divider",
};

// ── Expandable Table sub-component ──────────────────────────────
function ExpandableTable({ title, columns, rows, renderRow, expanded, onToggle }) {
  const visibleRows = expanded ? rows : rows.slice(0, INITIAL_ROWS);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ px: 2, pt: 1.5, pb: 1 }}
      >
        {title}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col, i) => (
              <TableCell
                key={col}
                sx={headerCellSx}
                align={i === 0 ? "left" : "right"}
              >
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{visibleRows.map(renderRow)}</TableBody>
      </Table>

      {rows.length > INITIAL_ROWS && (
        <Box sx={{ px: 2, py: 1, textAlign: "center" }}>
          <Button
            size="small"
            onClick={onToggle}
            endIcon={
              expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
            }
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            {expanded ? "Show less" : `Show all ${rows.length}`}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

// ── "All" View: Donut chart ─────────────────────────────────────
function AllView({ summary }) {
  const pieData = useMemo(() => {
    return (summary?.breakdown || [])
      .filter((item) => item.costType !== "Cost of Care")
      .map((item, idx) => ({
        id: item.costType.replace(" Cost", ""),
        value: item.cost,
        label: item.costType.replace(" Cost", ""),
        percentage: item.percentage,
        color: COST_CHART_COLORS[idx % COST_CHART_COLORS.length],
      }));
  }, [summary]);

  return (
    <ChartCard title="Cost Distribution" height={300}>
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        <ResponsivePie
          data={pieData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          innerRadius={0.6}
          padAngle={1}
          cornerRadius={4}
          activeOuterRadiusOffset={6}
          colors={COST_CHART_COLORS}
          borderWidth={0}
          enableArcLinkLabels={false}
          enableArcLabels={false}
          tooltip={({ datum }) => (
            <Paper
              elevation={3}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                fontSize: "0.8rem",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {datum.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCost(datum.value)} ({datum.data.percentage}%)
              </Typography>
            </Paper>
          )}
        />
        {/* Center text */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ lineHeight: 1.2 }}
          >
            {formatCost(summary?.totalCostOfCare)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total
          </Typography>
        </Box>
      </Box>
    </ChartCard>
  );
}

// ── "Monthly" View: Line chart ──────────────────────────────────
function MonthlyView({ monthly }) {
  const lineData = useMemo(() => {
    const months = monthly?.months || [];
    return [
      {
        id: "Total",
        color: COST_LINE_COLORS.total,
        data: months.map((m) => ({
          x: formatMonthLabel(m.month),
          y: m.total,
        })),
      },
      {
        id: "Inpatient",
        color: COST_LINE_COLORS.inpatient,
        data: months.map((m) => ({
          x: formatMonthLabel(m.month),
          y: m.inpatient,
        })),
      },
      {
        id: "Pharmacy",
        color: COST_LINE_COLORS.pharmacy,
        data: months.map((m) => ({
          x: formatMonthLabel(m.month),
          y: m.pharmacy,
        })),
      },
    ];
  }, [monthly]);

  return (
    <ChartCard title="Monthly Cost Trend" height={300}>
      <ResponsiveLine
        data={lineData}
        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
        axisBottom={{
          tickRotation: -45,
          legendOffset: 40,
          tickSize: 5,
          tickPadding: 5,
        }}
        axisLeft={{
          format: (v) => formatCost(v),
          tickSize: 5,
          tickPadding: 5,
        }}
        colors={[COST_LINE_COLORS.total, COST_LINE_COLORS.inpatient, COST_LINE_COLORS.pharmacy]}
        pointSize={6}
        pointBorderWidth={2}
        pointBorderColor={{ from: "serieColor" }}
        pointColor="#fff"
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <Paper
            elevation={3}
            sx={{ px: 1.5, py: 1, borderRadius: 2, fontSize: "0.8rem" }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
              {slice.points[0]?.data.xFormatted}
            </Typography>
            {slice.points.map((point) => (
              <Stack
                key={point.id}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: point.serieColor,
                  }}
                />
                <Typography variant="body2">
                  {point.serieId}: {formatCost(point.data.yFormatted)}
                </Typography>
              </Stack>
            ))}
          </Paper>
        )}
        useMesh
        legends={[
          {
            anchor: "top-right",
            direction: "row",
            translateY: -15,
            itemWidth: 80,
            itemHeight: 14,
            itemTextColor: "#666",
            symbolSize: 8,
            symbolShape: "circle",
          },
        ]}
      />
    </ChartCard>
  );
}

// ── "By Location" View: Horizontal bar chart ────────────────────
function ByLocationView({ byLocation }) {
  const barData = useMemo(() => {
    const locations = byLocation?.locations || [];
    return [...locations]
      .sort((a, b) => b.totalCost - a.totalCost)
      .map((loc) => {
        const other = loc.totalCost - (loc.inpatient || 0) - (loc.pharmacy || 0) - (loc.er || 0);
        return {
          location: loc.locationName,
          Inpatient: loc.inpatient || 0,
          Pharmacy: loc.pharmacy || 0,
          ER: loc.er || 0,
          Other: Math.max(0, other),
        };
      });
  }, [byLocation]);

  return (
    <ChartCard title="Cost by Location" height={Math.max(300, barData.length * 40 + 60)}>
      <ResponsiveBar
        data={barData}
        keys={["Inpatient", "Pharmacy", "ER", "Other"]}
        indexBy="location"
        layout="horizontal"
        margin={{ top: 10, right: 20, bottom: 40, left: 120 }}
        padding={0.3}
        colors={[
          COST_BAR_COLORS.inpatient,
          COST_BAR_COLORS.pharmacy,
          COST_BAR_COLORS.er,
          COST_BAR_COLORS.other,
        ]}
        borderRadius={2}
        axisBottom={{
          format: (v) => formatCost(v),
          tickSize: 5,
          tickPadding: 5,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
        }}
        enableLabel={false}
        tooltip={({ id, value, indexValue, color }) => (
          <Paper
            elevation={3}
            sx={{ px: 1.5, py: 1, borderRadius: 2, fontSize: "0.8rem" }}
          >
            <Typography variant="body2" fontWeight={700}>
              {indexValue}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: color,
                }}
              />
              <Typography variant="body2">
                {id}: {formatCost(value)}
              </Typography>
            </Stack>
          </Paper>
        )}
        legends={[
          {
            dataFrom: "keys",
            anchor: "bottom",
            direction: "row",
            translateY: 36,
            itemWidth: 90,
            itemHeight: 14,
            itemTextColor: "#666",
            symbolSize: 10,
            symbolShape: "square",
          },
        ]}
      />
    </ChartCard>
  );
}
